/**
 * Compact asset card for the horizontal scroll strip.
 * Shows media preview + asset ID + status by default; name/placement
 * behind a "Show details" toggle. All text is small (b100) to fit the card.
 */
import React, { useState } from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import type { PublisherEntityDto } from 'src/store/generated/leela-api-generated';
import {
  type GoogleAssetMetadata,
  narrowAssetMetadata,
} from 'src/utils/publisherEntityMetadata';
import {
  BADGE_LABELS,
  PublisherEntityBadge,
  derivePublisherEntityStatus,
} from 'src/utils/publisherEntityStatus';
import { EMPTY_VALUE } from './StatusBadge';

const STATUS_PILL_STYLES: Record<PublisherEntityBadge, { bg: string; text: string }> = {
  [PublisherEntityBadge.LIVE]: { bg: '#dcfce7', text: '#166534' },
  [PublisherEntityBadge.PENDING]: { bg: '#fef9c3', text: '#854d0e' },
  [PublisherEntityBadge.FAILED]: { bg: '#fee2e2', text: '#991b1b' },
  [PublisherEntityBadge.SKIPPED]: { bg: '#e0e7ff', text: '#3730a3' },
  [PublisherEntityBadge.REMOVED]: { bg: '#f3f4f6', text: '#374151' },
};

const StatusPill: React.FC<{ entity: PublisherEntityDto }> = ({ entity }) => {
  const { t } = useTranslation();
  const status = derivePublisherEntityStatus(entity);
  const style = STATUS_PILL_STYLES[status];
  return (
    <span style={{ backgroundColor: style.bg, borderRadius: 12, color: style.text, display: 'inline-block', fontSize: 12, fontWeight: 500, padding: '2px 8px', whiteSpace: 'nowrap' }}>
      {t(BADGE_LABELS[status])}
    </span>
  );
};

const MEDIA_HEIGHT = 200;
const CARD_WIDTH = 360;
const MEDIA_STYLE: React.CSSProperties = {
  display: 'block',
  height: '100%',
  maxHeight: '100%',
  maxWidth: '100%',
  objectFit: 'contain',
};

interface EntityAssetCardProps {
  entity: PublisherEntityDto;
}

interface AssetMediaProps {
  entityId: string;
  metadata: GoogleAssetMetadata | undefined;
}

const AssetMedia: React.FC<AssetMediaProps> = ({ entityId, metadata }) => {
  const [failedAssetUrl, setFailedAssetUrl] = useState<string>();
  const { t } = useTranslation();
  const { assetType, assetUrl } = metadata ?? {};
  const hasUsableMedia = assetUrl !== undefined && failedAssetUrl !== assetUrl;

  if (!hasUsableMedia || (assetType !== 'IMAGE' && assetType !== 'VIDEO')) {
    return (
      <div
        data-testid={`entity-asset-placeholder-${entityId}`}
        style={{ alignItems: 'center', background: '#1a1a1a', display: 'flex', height: MEDIA_HEIGHT, justifyContent: 'center', width: '100%' }}
      >
        <Text color="inverted" type="b200">{assetType === 'VIDEO' ? '▶' : '🖼'}</Text>
      </div>
    );
  }

  const handleMediaError = (): void => { setFailedAssetUrl(assetUrl); };

  return (
    <div style={{ alignItems: 'center', background: '#1a1a1a', display: 'flex', height: MEDIA_HEIGHT, justifyContent: 'center', width: '100%' }}>
      {assetType === 'IMAGE'
        ? (
          <img
            alt={t('ads_manager_asset_image_alt')}
            data-testid={`entity-asset-image-${entityId}`}
            src={assetUrl}
            style={MEDIA_STYLE}
            onError={handleMediaError}
          />
          )
        : (
          <video
            controls
            data-testid={`entity-asset-video-${entityId}`}
            muted
            preload="none"
            src={assetUrl}
            style={MEDIA_STYLE}
            onError={handleMediaError}
          />
          )}
    </div>
  );
};

export const EntityAssetCard: React.FC<EntityAssetCardProps> = ({ entity }) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const metadata = narrowAssetMetadata(entity.publisherMetadata);

  return (
    <Box
      data-testid={`entity-asset-card-${entity._id}`}
      overflowX="hidden"
      overflowY="hidden"
      type="outline"
      width={CARD_WIDTH}
    >
      <AssetMedia entityId={entity._id} metadata={metadata} />
      <Box spacingInset="300">
        <Column spacing="100">
          <Row alignmentHorizontal="justify" alignmentVertical="center" spacing="200">
            <Text color="secondary" type="b100">{entity.sourceEntityId ?? EMPTY_VALUE}</Text>
            <StatusPill entity={entity} />
          </Row>

          <button
            data-testid={`entity-asset-details-toggle-${entity._id}`}
            style={{ background: 'none', border: 'none', color: '#0972d3', cursor: 'pointer', fontSize: '12px', padding: 0, textAlign: 'left' }}
            onClick={() => { setShowDetails(prev => !prev); }}
          >
            {t('ads_manager_details')}
          </button>

          {showDetails && (
            <Column spacing="200">
              {metadata?.name != null && (
                <Column spacing="none">
                  <Text color="secondary" type="b100">{t('form_global_name')}</Text>
                  <Text type="b100">{metadata.name}</Text>
                </Column>
              )}
              {metadata?.placement != null && (
                <Column spacing="none">
                  <Text color="secondary" type="b100">{t('ads_manager_asset_placement')}</Text>
                  <Text type="b100">{metadata.placement}</Text>
                </Column>
              )}
              {entity.publisherEntityId != null && (
                <Column spacing="none">
                  <Text color="secondary" type="b100">{t('ads_manager_col_entity_id')}</Text>
                  <Text type="b100">{entity.publisherEntityId}</Text>
                </Column>
              )}
              {entity.lastError != null && (
                <Column spacing="none">
                  <Text color="secondary" type="b100">{t('ads_manager_col_last_error')}</Text>
                  <Text color="error" type="b100">{entity.lastError}</Text>
                </Column>
              )}
            </Column>
          )}
        </Column>
      </Box>
    </Box>
  );
};
